import React, { useState } from 'react';
import { Translate, useTranslate } from '~/i18n';
import { DateTime } from 'luxon';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Swiper, SwiperSlide } from 'swiper/react';
import cn from 'classnames';
import { Button } from '~/components/common/button';
import RatingStars from '~/components/common/rating-stars';
import Dropdown from '~/components/common/dropdown';
import { CalendarIconOutline, UserIcon, ArrowLeft, ArrowRight } from '~/svgs';
import { ReviewBubbleIcon } from '~/svgs/header';
import { WarningOutlineIcon } from '~/svgs/marketplace';
import { ReviewProps } from '~/interfaces/review';
import ReviewReplyModal from './reply-modal';
import ReportAbusePopup from './report-abuse-modal';

const ReviewItem = ({ review, refetchReviews }: { review: ReviewProps; refetchReviews: any }) => {
   const { lang, dir } = useTranslate();
   const router = useRouter();
   const [replyFor, setReplyFor] = useState<any>(undefined);
   const [openReportAbuseAlert, setOpenReportAbuseAlert] = useState(false);
   const [selectedReportData, setSelectedReportData] = useState<any>(undefined);
   const [showMoreOptions, setShowMoreOptions] = useState('');
   const moreOptionsToggler = (review_id?: string) => setShowMoreOptions(review_id || '');
   const hideMoreOptions = () => setShowMoreOptions('');

   return (
      <div key={review.id} className="border-b py-3">
         <div className="flex flex-col lg:flex-row justify-between w-full gap-4">
            <div className="w-full lg:w-52 flex gap-4">
               <div>
                  {review?.user?.thumbnail ? (
                     <Image
                        src={review?.user?.thumbnail}
                        width={60}
                        height={60}
                        className="rounded-full"
                        layout="fixed"
                     />
                  ) : (
                     <div className="flex bg-grey-500 p-5 rounded-full w-[60px] h-[60px] items-center justify-center">
                        <UserIcon width={30} height={30} />
                     </div>
                  )}
               </div>
               <div className="flex flex-col">
                  <span className="font-semibold mb-2">{review?.user?.name}</span>
                  <div className="flex flex-wrap text-sm">
                     <span className="text-grey-200">
                        <Translate id="bookings:product" />:
                     </span>
                     <span className="font-semibold">{review?.product_name}</span>
                  </div>
                  <div className="flex flex-wrap text-sm">
                     <span className="text-grey-200">
                        <Translate id="reviews:booking id" />:
                     </span>
                     <span className="font-semibold">{review?.booking_id}</span>
                  </div>
               </div>
            </div>
            <div className="w-full lg:w-1/2">
               <div className="flex items-center gap-5">
                  <RatingStars rating_value={review?.overall_rating} color="white" />
                  <span className="text-grey-200">
                     {DateTime.fromJSDate(new Date(review?.created_at.replace(' ', 'T')))
                        .setLocale(lang)
                        .toFormat('dd - MM - yyyy')}
                  </span>
                  {review?.overall_rating < 2 ? (
                     <div className="bg-red text-sm text-white py-1.5 px-4 rounded-full">
                        <Translate id="reviews:requires attention" />
                     </div>
                  ) : null}
               </div>
               {review?.liked_comment ? (
                  <div>
                     <span className="text-sm font-semibold">
                        <Translate id="reviews:what i liked" />:
                     </span>
                     <p>{review?.liked_comment}</p>
                  </div>
               ) : null}
               {review?.not_liked_comment ? (
                  <div>
                     <span className="text-sm font-semibold">
                        <Translate id="reviews:what i didn't liked" />:
                     </span>
                     <p>{review?.not_liked_comment}</p>
                  </div>
               ) : null}

               {review?.photos?.length ? (
                  <div className="relative mt-3 !z-0 overflow-hidden max-w-[100vw] lg:w-full">
                     <Swiper
                        key={`${dir}_review_${review?.id}`}
                        slidesPerView={1}
                        spaceBetween={1}
                        dir={dir}
                        navigation={{
                           nextEl: `.next-${review?.id}`,
                           prevEl: `.prev-${review?.id}`,
                        }}>
                        {review?.photos?.map((image: any, idx: any) => (
                           <SwiperSlide key={idx} className="xs:p-0">
                              <div className="relative h-52">
                                 <div
                                    className="relative h-full overflow-hidden transition-transform duration-500 ease-in-out delay-200"
                                    style={{ position: 'relative' }}>
                                    <Image
                                       layout="fill"
                                       className="object-cover rounded-lg transition-transform duration-500 delay-200 cursor-pointer"
                                       src={image}
                                       alt="image"
                                       quality={60}
                                    />
                                 </div>
                              </div>
                           </SwiperSlide>
                        ))}
                     </Swiper>
                     <div className="flex h-full z-10 absolute top-1/2 w-full px-3 justify-between">
                        <button
                           className={cn(
                              'swiper-arrow flex justify-center items-center bg-opacity-50 bg-white rounded-full h-8 w-8 focus:outline-none focus:ring-0',
                              `prev-${review?.id}`
                           )}
                           aria-label="previous">
                           {dir === 'ltr' ? <ArrowLeft /> : <ArrowRight />}
                        </button>
                        <button
                           className={cn(
                              'swiper-arrow flex justify-center items-center bg-opacity-50 bg-white rounded-full h-8 w-8 focus:outline-none focus:ring-0',
                              `next-${review?.id}`
                           )}
                           aria-label="next">
                           {dir === 'ltr' ? <ArrowRight /> : <ArrowLeft />}
                        </button>
                     </div>
                  </div>
               ) : null}

               {/* REPLY */}
               {review.reply && review.reply.id ? (
                  <div className="mt-5 bg-grey-500 p-4 rounded-md ">
                     <p className="text-base mb-2">{review.reply.content}</p>
                     <p className="text-sm text-grey-200 font-semibold">
                        <Translate id="reviews:replied on" />{' '}
                        {DateTime.fromJSDate(new Date(review.reply.created_at))
                           .setLocale(lang)
                           .toFormat('MMMM dd')}
                     </p>
                  </div>
               ) : null}
            </div>
            <div className="w-full lg:w-60 flex gap-4">
               <Button
                  onClick={() => {
                     setReplyFor(review);
                  }}
                  className="!px-2 !w-36 h-10 flex gap-4 items-center focus:!outline-transparent focus:!ring-0"
                  theme="primary">
                  <ReviewBubbleIcon width={20} height={20} className="[&>*]:fill-white" />
                  <span>
                     <Translate id={`reviews:${review?.reply?.id ? 'edit reply' : 'reply'}`} />
                  </span>
               </Button>
               <Dropdown
                  toggleMenu={hideMoreOptions}
                  listClassName="!top-14 !rounded-md"
                  toggler={
                     <Button
                        onClick={() =>
                           moreOptionsToggler(showMoreOptions?.length ? '' : review?.id)
                        }
                        className="w-full !px-5 h-10 focus:!outline-transparent focus:!ring-0"
                        theme="outline-primary">
                        <div>
                           <Translate id="reviews:more" />
                        </div>
                     </Button>
                  }
                  open={showMoreOptions === review?.id}>
                  <div className="w-72">
                     <div className="flex flex-col">
                        <Button
                           onClick={() => {
                              moreOptionsToggler();
                              router.push(`/${lang}/bookings/${review?.booking_id}`);
                           }}
                           className="w-full flex gap-3 !text-sm !border-none !outline-transparent !ring-0">
                           <CalendarIconOutline
                              width={20}
                              height={20}
                              className="[&>*]:fill-black"
                           />
                           <span className="text-base">
                              <Translate id="reviews:go to this booking" />
                           </span>
                        </Button>
                        <Button
                           onClick={() => {
                              moreOptionsToggler();
                              setSelectedReportData({
                                 review_id: review?.id,
                                 reported_message: JSON.stringify({
                                    liked_comment: review?.liked_comment,
                                    not_liked_comment: review?.not_liked_comment,
                                 }),
                              });
                              setOpenReportAbuseAlert(true);
                           }}
                           className="w-full flex gap-3 !text-sm !border-none !outline-transparent !ring-0">
                           <WarningOutlineIcon width={20} height={20} className="[&>*]:fill-red" />
                           <span className="text-red text-base">
                              <Translate id="reviews:report abusive comment" />
                           </span>
                        </Button>
                     </div>
                  </div>
               </Dropdown>
            </div>
         </div>

         {replyFor?.id === review?.id ? (
            <ReviewReplyModal
               open={replyFor?.id === review?.id}
               review={replyFor}
               refetchReviews={refetchReviews}
               closeModal={() => {
                  setReplyFor(undefined);
               }}
            />
         ) : null}
         {selectedReportData?.review_id ? (
            <ReportAbusePopup
               open={openReportAbuseAlert}
               reportData={selectedReportData}
               onClose={() => {
                  setSelectedReportData(undefined);
                  setOpenReportAbuseAlert(false);
               }}
               onConfirm={() => {
                  setSelectedReportData(undefined);
                  setOpenReportAbuseAlert(false);
               }}
            />
         ) : null}
      </div>
   );
};

export default ReviewItem;
